import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  MagnifyingGlass as Search,
  Buildings as Building2,
  Scales as Scale,
  MapPin,
  Plus,
  PencilSimple,
  Trash,
  X,
} from '@phosphor-icons/react';
import { PageHeader } from '@/components/shared/PageHeader';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  fetchCourts, fetchJudges, createCourt, updateCourt, deleteCourt,
  createJudge, updateJudge, deleteJudge,
  COURT_TYPE_LABELS, type Court, type Judge, type CourtType,
} from '@/lib/courtsApi';
import { getApiErrorMessage } from '@/lib/api';
import { ScrollLock } from '@/components/shared/ScrollLock';

type Tab = 'courts' | 'judges';
const COURT_TYPES = Object.keys(COURT_TYPE_LABELS) as CourtType[];

export default function CourtsPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>('courts');
  const [searchQuery, setSearchQuery] = useState('');
  const [editCourt, setEditCourt] = useState<Court | 'new' | null>(null);
  const [editJudge, setEditJudge] = useState<Judge | 'new' | null>(null);

  const courtsQ = useQuery({ queryKey: ['courts'], queryFn: () => fetchCourts() });
  const judgesQ = useQuery({ queryKey: ['judges'], queryFn: () => fetchJudges() });
  const courts = courtsQ.data ?? [];
  const judges = judgesQ.data ?? [];

  const delCourt = useMutation({ mutationFn: (id: string) => deleteCourt(id), onSuccess: () => { toast.success('Court deleted'); qc.invalidateQueries({ queryKey: ['courts'] }); }, onError: (e) => toast.error(getApiErrorMessage(e, 'Could not delete')) });
  const delJudge = useMutation({ mutationFn: (id: string) => deleteJudge(id), onSuccess: () => { toast.success('Judge deleted'); qc.invalidateQueries({ queryKey: ['judges'] }); }, onError: (e) => toast.error(getApiErrorMessage(e, 'Could not delete')) });

  const s = searchQuery.toLowerCase();
  const fCourts = useMemo(() => (s ? courts.filter((c) => c.name.toLowerCase().includes(s) || c.shortName.toLowerCase().includes(s) || (c.city ?? '').toLowerCase().includes(s)) : courts), [courts, s]);
  const fJudges = useMemo(() => (s ? judges.filter((j) => j.fullName.toLowerCase().includes(s)) : judges), [judges, s]);

  return (
    <div>
      <PageHeader
        onBack={() => navigate(-1)}
        title="Courts & Judges"
        subtitle="Your chambers' courts and judiciary — used across cases, hearings and judgments. Add, edit or remove your own."
        actions={
          <Button variant="primary" size="md" leftIcon={<Plus className="h-4 w-4" />} onClick={() => (tab === 'courts' ? setEditCourt('new') : setEditJudge('new'))}>
            {tab === 'courts' ? 'Add court' : 'Add judge'}
          </Button>
        }
      />

      <div className="mb-4 flex items-center gap-1 border-b border-border">
        {([{ key: 'courts' as const, label: 'Courts', count: courts.length }, { key: 'judges' as const, label: 'Judges', count: judges.length }]).map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} className={`-mb-px border-b-2 px-4 py-2.5 text-sm font-medium transition ${tab === t.key ? 'border-brand-600 text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
            {t.label}<span className="ml-1.5 rounded-full bg-muted px-1.5 py-0.5 text-[10px] tabular text-muted-foreground">{t.count}</span>
          </button>
        ))}
      </div>

      <Card className="mb-4 p-3">
        <div className="flex items-center gap-2 rounded-md border border-border bg-card px-3">
          <Search className="h-4 w-4 text-muted-foreground" />
          <input type="search" placeholder={tab === 'courts' ? 'Search courts…' : 'Search judges…'} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="h-9 flex-1 bg-transparent text-sm placeholder:text-muted-foreground focus:outline-none" />
        </div>
      </Card>

      {tab === 'courts' ? (
        <Card>
          {courtsQ.isLoading ? (
            <div className="py-12 text-center text-sm text-muted-foreground">Loading…</div>
          ) : fCourts.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">{searchQuery ? 'No courts match' : 'No courts yet — add one.'}</div>
          ) : (
            <div className="overflow-x-auto">
              {/* A table cannot reflow: on a phone its right-hand columns ran off the screen with no way to reach them. */}
              <table className="data-table">
                <thead><tr><th>Code</th><th>Name</th><th>Type</th><th>Location</th><th>Matters</th><th></th></tr></thead>
                <tbody>
                  {fCourts.map((c) => (
                    <tr key={c.id}>
                      <td className="text-xs text-brand-700 dark:text-brand-300">{c.shortName}</td>
                      <td className="font-medium text-foreground"><Building2 className="mr-2 inline h-4 w-4 text-muted-foreground" />{c.name}{!c.isActive && <Badge className="ml-2">Inactive</Badge>}</td>
                      <td><Badge>{COURT_TYPE_LABELS[c.type] ?? c.type}</Badge></td>
                      <td className="text-muted-foreground">{c.city && <><MapPin className="mr-1 inline h-3 w-3" />{c.city}</>}</td>
                      <td className="text-xs text-muted-foreground">{c.caseCount}</td>
                      <td className="text-right">
                        <button onClick={() => setEditCourt(c)} className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"><PencilSimple className="h-4 w-4" /></button>
                        <button onClick={() => { if (confirm(`Delete ${c.name}?`)) delCourt.mutate(c.id); }} className="rounded p-1.5 text-muted-foreground hover:bg-red-50 dark:bg-red-500/10 hover:text-red-600 dark:hover:text-red-400 dark:hover:bg-red-950/40"><Trash className="h-4 w-4" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      ) : (
        <Card>
          {judgesQ.isLoading ? (
            <div className="py-12 text-center text-sm text-muted-foreground">Loading…</div>
          ) : fJudges.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">{searchQuery ? 'No judges match' : 'No judges yet — add one.'}</div>
          ) : (
            <div className="overflow-x-auto">
              {/* A table cannot reflow: on a phone its right-hand columns ran off the screen with no way to reach them. */}
              <table className="data-table">
                <thead><tr><th>Name</th><th>Court</th><th>Appointed</th><th>Matters</th><th></th></tr></thead>
                <tbody>
                  {fJudges.map((j) => (
                    <tr key={j.id}>
                      <td className="font-medium text-foreground"><Scale className="mr-2 inline h-4 w-4 text-muted-foreground" />{j.title} {j.fullName}</td>
                      <td className="text-muted-foreground">{j.court?.shortName ?? '—'}</td>
                      <td className="text-xs text-muted-foreground">{j.appointedYear ?? '—'}</td>
                      <td className="text-xs text-muted-foreground">{j.caseCount}</td>
                      <td className="text-right">
                        <button onClick={() => setEditJudge(j)} className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"><PencilSimple className="h-4 w-4" /></button>
                        <button onClick={() => { if (confirm(`Delete ${j.title} ${j.fullName}?`)) delJudge.mutate(j.id); }} className="rounded p-1.5 text-muted-foreground hover:bg-red-50 dark:bg-red-500/10 hover:text-red-600 dark:hover:text-red-400 dark:hover:bg-red-950/40"><Trash className="h-4 w-4" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {editCourt && <CourtEditor court={editCourt === 'new' ? null : editCourt} onClose={() => setEditCourt(null)} onSaved={() => { qc.invalidateQueries({ queryKey: ['courts'] }); setEditCourt(null); }} />}
      {editJudge && <JudgeEditor judge={editJudge === 'new' ? null : editJudge} courts={courts} onClose={() => setEditJudge(null)} onSaved={() => { qc.invalidateQueries({ queryKey: ['judges'] }); setEditJudge(null); }} />}
    </div>
  );
}

function Modal({ title, onClose, children, footer }: { title: string; onClose: () => void; children: React.ReactNode; footer: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4" onClick={onClose}>
      <ScrollLock />
      <div className="my-auto w-full max-w-md rounded-lg border border-border bg-card shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <span className="text-sm font-semibold text-foreground">{title}</span>
          <button onClick={onClose} className="rounded p-1 text-muted-foreground hover:bg-muted"><X className="h-4 w-4" /></button>
        </div>
        <div className="space-y-4 px-5 py-4">{children}</div>
        <div className="flex items-center justify-end gap-2 border-t border-border px-5 py-3">{footer}</div>
      </div>
    </div>
  );
}

const inputCls = 'h-9 w-full rounded-lg border border-border bg-card px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-brand-400';

function CourtEditor({ court, onClose, onSaved }: { court: Court | null; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState(court?.name ?? '');
  const [shortName, setShortName] = useState(court?.shortName ?? '');
  const [type, setType] = useState<CourtType>(court?.type ?? COURT_TYPES[0]!);
  const [city, setCity] = useState(court?.city ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    if (name.trim().length < 2 || !shortName.trim()) { setError('Name and code are required'); return; }
    setSaving(true); setError(null);
    try {
      const input = { name: name.trim(), shortName: shortName.trim(), type, city: city.trim() || undefined };
      if (court) await updateCourt(court.id, input); else await createCourt(input);
      toast.success(court ? 'Court saved' : 'Court added');
      onSaved();
    } catch (e) { setError(getApiErrorMessage(e, 'Could not save')); } finally { setSaving(false); }
  };

  return (
    <Modal title={court ? 'Edit court' : 'Add court'} onClose={onClose} footer={<><Button variant="outline" size="sm" onClick={onClose}>Cancel</Button><Button variant="primary" size="sm" onClick={save} disabled={saving}>{saving ? 'Saving…' : court ? 'Save' : 'Add'}</Button></>}>
      <label className="block"><span className="mb-1.5 block text-xs font-medium text-muted-foreground">Name</span><input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Central London County Court" className={inputCls} /></label>
      <div className="grid grid-cols-2 gap-3">
        <label className="block"><span className="mb-1.5 block text-xs font-medium text-muted-foreground">Code</span><input value={shortName} onChange={(e) => setShortName(e.target.value)} placeholder="e.g. CLCC" className={inputCls} /></label>
        <label className="block"><span className="mb-1.5 block text-xs font-medium text-muted-foreground">Type</span><select value={type} onChange={(e) => setType(e.target.value as CourtType)} className={inputCls}>{COURT_TYPES.map((t) => <option key={t} value={t}>{COURT_TYPE_LABELS[t]}</option>)}</select></label>
      </div>
      <label className="block"><span className="mb-1.5 block text-xs font-medium text-muted-foreground">City (optional)</span><input value={city} onChange={(e) => setCity(e.target.value)} placeholder="e.g. London" className={inputCls} /></label>
      {error && <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300">{error}</div>}
    </Modal>
  );
}

function JudgeEditor({ judge, courts, onClose, onSaved }: { judge: Judge | null; courts: Court[]; onClose: () => void; onSaved: () => void }) {
  const [title, setTitle] = useState(judge?.title ?? '');
  const [fullName, setFullName] = useState(judge?.fullName ?? '');
  const [courtId, setCourtId] = useState(judge?.court?.id ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    if (title.trim().length < 2 || fullName.trim().length < 2) { setError('Title and full name are required'); return; }
    setSaving(true); setError(null);
    try {
      const surname = fullName.trim().split(/\s+/).slice(-1)[0];
      const input = { title: title.trim(), fullName: fullName.trim(), shortName: `${title.trim()} ${surname}`, courtId: courtId || undefined };
      if (judge) await updateJudge(judge.id, input); else await createJudge(input);
      toast.success(judge ? 'Judge saved' : 'Judge added');
      onSaved();
    } catch (e) { setError(getApiErrorMessage(e, 'Could not save')); } finally { setSaving(false); }
  };

  return (
    <Modal title={judge ? 'Edit judge' : 'Add judge'} onClose={onClose} footer={<><Button variant="outline" size="sm" onClick={onClose}>Cancel</Button><Button variant="primary" size="sm" onClick={save} disabled={saving}>{saving ? 'Saving…' : judge ? 'Save' : 'Add'}</Button></>}>
      <div className="grid grid-cols-3 gap-3">
        <label className="block"><span className="mb-1.5 block text-xs font-medium text-muted-foreground">Title</span><input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="HHJ" className={inputCls} /></label>
        <label className="col-span-2 block"><span className="mb-1.5 block text-xs font-medium text-muted-foreground">Full name</span><input value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Jane Smith" className={inputCls} /></label>
      </div>
      <label className="block"><span className="mb-1.5 block text-xs font-medium text-muted-foreground">Court (optional)</span><select value={courtId} onChange={(e) => setCourtId(e.target.value)} className={inputCls}><option value="">— none —</option>{courts.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></label>
      <p className="text-xs text-muted-foreground">Judges are identified by title + surname (e.g. “HHJ Smith”) — no ID number, matching UK practice.</p>
      {error && <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300">{error}</div>}
    </Modal>
  );
}
